import { logger } from "@-/dev.improve.haniverse.lib.logger";
import delay from "@-/com.npmjs.delay";
import { withDeadline } from "@-/dev.improve.haniverse.lib.deadline";

// One native `pull()` fetches and applies a single batch. A request the server or network never
// answers must fail this attempt (and let the caller's retry/idle deadline decide) rather than
// hold the drain — and the sync queue and backup coordination it runs inside — open.
const PULL_CALL_TIMEOUT_MS = 30_000;

export async function drainActorTursoPull(
  sync: { pull(): Promise<boolean> },
  onChangesApplied: () => void,
  isDisposed: () => boolean,
  onBatchApplied?: () => Promise<void>,
): Promise<boolean> {
  const MAX_PULL_BATCHES = 64;
  // Turso returns false only when its revision cursor has no unapplied changes.
  // One empty response is authoritative; another request and a 500ms sleep add
  // latency without making this snapshot any fresher. Later writes are handled
  // by notifications and foreground/reconnect catch-up.
  let totalApplied = 0;

  try {
    for (let batch = 0; batch < MAX_PULL_BATCHES && !isDisposed(); batch++) {
      const appliedChanges = await withDeadline(
        { deadline: PULL_CALL_TIMEOUT_MS, operation: "Actor Turso pull()" },
        () => sync.pull(),
      );
      if (isDisposed()) {
        throw new Error("Actor Turso sync is disconnected.");
      }
      logger(import.meta.location).info("remote-sync:pull-batch", { appliedChanges, batch });
      if (!appliedChanges) {
        return totalApplied > 0;
      }
      totalApplied += 1;
      if (totalApplied === 1) {
        onChangesApplied();
      }
      await onBatchApplied?.();
      // Yield a task between batches so applied rows can render before the next request.
      await delay(0);
    }

    if (isDisposed()) {
      throw new Error("Actor Turso sync is disconnected.");
    }
    throw new Error(
      "Actor Turso pull exceeded its batch budget before reaching the remote revision.",
    );
  } finally {
    // Publish later batches even when the drain fails before its empty response.
    // They committed locally and must not stay invisible to the query cache.
    if (totalApplied > 1 && !isDisposed()) {
      onChangesApplied();
    }
  }
}
